import {DatePipe, NgTemplateOutlet} from '@angular/common';
import {Component, Input, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ContractModel} from '../../../models/contract.model';
import {PaginationModel} from '../../../models/file-table.model';
import {MemberModel} from '../../../models/user.model';
import {ContractsService} from '../../../services/api/contracts.service';
import {AlertService} from '../../../services/notify/alert.service';
import {StatisticService} from '../../../services/statistic.service';
import {CalendarItem, CalendarLegend} from '../../calendar/calendar.model';
import {ComponentsModule} from '../../components.module';
import {buildItemsMap} from '../../calendar/calendar-utils';

type ContractStatistic = {
    closed: number,
    completed: number,
    open: number,
    verified: number,
}

@Component({
    selector: 'app-contracts-index-tab',
    standalone: true,
    imports: [
        ComponentsModule,
        NgTemplateOutlet,
        DatePipe,
        TranslatePipe
    ],
    templateUrl: './contracts-index-tab.component.html',
    styleUrl: './contracts-index-tab.component.scss'
})
export class ContractsIndexTabComponent implements OnInit {
    @Input() currentMember: MemberModel;

    public pagination: PaginationModel = new PaginationModel();

    allContracts: ContractModel[] = [];

    allParsedContracts: {
        contractsTab: 'allContracts' | 'newContracts' | 'workContracts' | 'completedContracts' | 'archivedContracts',
        allContracts: ContractModel[],
        newContracts: ContractModel[],
        workContracts: ContractModel[],
        completedContracts: ContractModel[],
        archivedContracts: ContractModel[],
    };

    tab: 0 | 1 | 2 = 0;

    current = new Date();
    itemsMap: Record<string, CalendarItem[]> = {};

    calendarLegend: CalendarLegend[];

    statisticData: ContractStatistic;

    constructor(
        private contractService: ContractsService,
        private alertService: AlertService,
        private router: Router,
        private translate: TranslateService,
        private statistic: StatisticService,
    ) {
        this.pagination.sort_by = 'updated_at';
        this.pagination.sort = 'desc';
        this.pagination.current_page = 1;
        this.pagination.limit = 50;
        this.pagination.status = 'open,pre_verified,verified,completed';

        this.calendarLegend = [
            {
                type: 'light',
                title: this.translate.instant('INDEX_PAGE.CONTRACTS.TEXT.LEGEND.NEW'),
            },
            {
                type: 'blue',
                title: this.translate.instant('INDEX_PAGE.CONTRACTS.TEXT.LEGEND.IN_PROGRESS'),
            },
            {
                type: 'green',
                title: this.translate.instant('INDEX_PAGE.CONTRACTS.TEXT.LEGEND.COMPLETED'),
            },
            {
                type: 'red',
                title: this.translate.instant('INDEX_PAGE.CONTRACTS.TEXT.LEGEND.ARCHIVED'),
            }
        ];

        this.getStatistic();
    }

    ngOnInit(): void {
        this.getAllContracts();
    }

    getStatistic(): void {
        this.statistic.getIndexPageStatistics('contracts').subscribe({
            next: res => {
                this.statisticData = res.contracts as ContractStatistic;
            }
        });
    }

    getAllContracts(): void {
        this.contractService.getAllMembersContractatOnce(this.pagination, this.currentMember.guid, this.currentMember.project_setting?.is_project_admin)
            .subscribe({
                next: res => {
                    this.allContracts = res;
                    this.parseContracts();
                }, error: err => {
                    this.alertService.setAlert(err.message, 'blue');
                }
            });
    }

    parseContracts(): void {
        // TODO (максимально 300, потом будет статистика, переделать)
        const newStatuses = ['open'];
        const workStatuses = ['pre_verified', 'verified'];
        const completedStatuses = ['completed'];
        const archiveStatuses = ['closer'];

        this.allParsedContracts = {
            contractsTab: 'allContracts',
            allContracts: this.allContracts,
            newContracts: this.allContracts.filter(c => newStatuses.includes(c.status)),
            workContracts: this.allContracts.filter(c => workStatuses.includes(c.status)),
            completedContracts: this.allContracts.filter(c => completedStatuses.includes(c.status)),
            archivedContracts: this.allContracts.filter(c => archiveStatuses.includes(c.status)),
        };
    }

//TODO календарь (оптимизировать)
    onDayClick(date: Date) {
        console.log('day clicked', date);
    }

    onItemClick(payload: { date: Date; item: CalendarItem }) {
        console.log('item clicked', payload);
    }

    onAddItems(startDate: Date, endDate: Date): void {
        const contracts = this.allContracts;

        const filtered: (CalendarItem & { __key: string })[] = [];

        for (const contract of contracts) {
            const lastUpdateDate = new Date(new Date(contract.updated_at).toDateString());
            if (lastUpdateDate < startDate || lastUpdateDate > endDate) {
                continue;
            }

            let contractColor: 'light' | 'blue' | 'green' | 'red';
            if (['open'].includes(contract.status)) {
                contractColor = 'light';
            } else if (['verified', 'pre_verified'].includes(contract.status)) {
                contractColor = 'blue';
            } else if (['complete'].includes(contract.status)) {
                contractColor = 'green';
            } else {
                contractColor = 'red';
            }

            const y = lastUpdateDate.getFullYear();
            const m = (lastUpdateDate.getMonth() + 1).toString().padStart(2, '0');
            const d = lastUpdateDate.getDate().toString().padStart(2, '0');
            const key = `${y}-${m}-${d}`;

            filtered.push({
                __key: key,
                id: contract.guid,
                title: contract.name,
                value: contract.name,
                type: contractColor,
            } as any);
        }

        const newMap = buildItemsMap(filtered, this.itemsMap);

        if (newMap !== this.itemsMap) {
            this.itemsMap = newMap;
        }
    }

    goTo(contract: ContractModel): void {
        void this.router.navigate(['account/contracts/detail/', contract.guid]);
    }
}
